import { useFormContext } from "react-hook-form";
import { US_STATES } from "@/lib/constants";
import type { CheckoutFormData } from "@/pages/CheckoutPage";

interface AddressFieldsProps {
  prefix: "billingAddress" | "mailingAddress";
  label?: string;
  description?: string;
}

export function AddressFields({
  prefix,
  label,
  description,
}: AddressFieldsProps): React.ReactElement {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CheckoutFormData>();

  const addressErrors = errors[prefix];

  return (
    <fieldset className="space-y-3">
      {label && (
        <legend className="text-sm font-medium text-gray-700">{label}</legend>
      )}
      {description && <p className="text-xs text-gray-500">{description}</p>}

      <div>
        <label
          htmlFor={`${prefix}.street`}
          className="block text-sm font-medium text-gray-700"
        >
          Street Address
        </label>
        <input
          id={`${prefix}.street`}
          type="text"
          autoComplete="street-address"
          {...register(`${prefix}.street`)}
          className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            addressErrors?.street
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300"
          }`}
        />
        {addressErrors?.street?.message && (
          <p className="mt-1 text-xs text-red-600">
            {addressErrors.street.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-6 gap-3">
        <div className="col-span-3">
          <label
            htmlFor={`${prefix}.city`}
            className="block text-sm font-medium text-gray-700"
          >
            City
          </label>
          <input
            id={`${prefix}.city`}
            type="text"
            autoComplete="address-level2"
            {...register(`${prefix}.city`)}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              addressErrors?.city
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {addressErrors?.city?.message && (
            <p className="mt-1 text-xs text-red-600">
              {addressErrors.city.message}
            </p>
          )}
        </div>

        <div className="col-span-1">
          <label
            htmlFor={`${prefix}.state`}
            className="block text-sm font-medium text-gray-700"
          >
            State
          </label>
          <select
            id={`${prefix}.state`}
            autoComplete="address-level1"
            {...register(`${prefix}.state`)}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              addressErrors?.state
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          >
            <option value="">--</option>
            {US_STATES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.value}
              </option>
            ))}
          </select>
          {addressErrors?.state?.message && (
            <p className="mt-1 text-xs text-red-600">
              {addressErrors.state.message}
            </p>
          )}
        </div>

        <div className="col-span-2">
          <label
            htmlFor={`${prefix}.zip`}
            className="block text-sm font-medium text-gray-700"
          >
            ZIP Code
          </label>
          <input
            id={`${prefix}.zip`}
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            value={watch(`${prefix}.zip`) ?? ""}
            onChange={(e) => {
              setValue(
                `${prefix}.zip`,
                e.target.value.replace(/\D/g, "").slice(0, 5),
                { shouldValidate: true },
              );
            }}
            className={`mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              addressErrors?.zip
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300"
            }`}
          />
          {addressErrors?.zip?.message && (
            <p className="mt-1 text-xs text-red-600">
              {addressErrors.zip.message}
            </p>
          )}
        </div>
      </div>
    </fieldset>
  );
}
